/**
 * Répartir les commandes du jour entre livreurs (15/10/2026).
 *
 * « Générer tournées » prenait TOUTES les commandes prêtes du jour dans une
 * seule tournée. Le soir, Philippe en livre une partie et Paul l'autre : on
 * choisit maintenant les commandes de chaque tournée, et on peut en ajouter à
 * une tournée existante.
 */
import { useCallback, useEffect, useState } from 'react';

export interface CommandeAPlacer {
  id: string;
  order_number: string;
  customer_name: string;
  status: string;
  order_type?: string | null;
  placable: boolean;
  /** Prévue un jour passé (jour férié) : la tournée la reporte à son jour. */
  en_retard?: boolean;
  delivery_date?: string | null;
  delivery_address?: { street: string; postal_code: string; city: string } | null;
}

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

function raison(c: CommandeAPlacer): string {
  if (!c.delivery_address) return 'sans adresse';
  if (c.status !== 'ready') return 'pas encore prête';
  return '';
}

/** Charge les commandes à placer d'un jour, et la sélection de l'utilisateur. */
function useCommandesAPlacer(apiFetch: ApiFetch, date: string, actif: boolean) {
  const [commandes, setCommandes] = useState<CommandeAPlacer[] | null>(null);
  const [choisies, setChoisies] = useState<Set<string>>(new Set());
  const [erreur, setErreur] = useState('');

  const charger = useCallback(async () => {
    setErreur('');
    try {
      const r = await apiFetch(`/customer-deliveries/a-placer?date=${date}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const liste: CommandeAPlacer[] = await r.json();
      setCommandes(liste);
      // Tout ce qui peut partir est coché : on décoche ce qui va à l'autre livreur.
      setChoisies(new Set(liste.filter((c) => c.placable).map((c) => c.id)));
    } catch (e) {
      setCommandes([]);
      setErreur(e instanceof Error ? e.message : 'chargement impossible');
    }
  }, [apiFetch, date]);

  useEffect(() => { if (actif) void charger(); }, [actif, charger]);

  const basculer = (id: string) =>
    setChoisies((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  return { commandes, choisies, basculer, erreur, recharger: charger };
}

function ListeCommandes({ commandes, choisies, basculer }: {
  commandes: CommandeAPlacer[];
  choisies: Set<string>;
  basculer: (id: string) => void;
}) {
  if (commandes.length === 0) {
    return <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-4 py-3">Aucune commande à livrer ce jour-là (ni en retard) en dehors des tournées.</p>;
  }
  return (
    <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
      {commandes.map((c) => (
        <li key={c.id}>
          <label className={`flex items-start gap-3 px-4 py-3 ${c.placable ? 'cursor-pointer hover:bg-gray-50' : 'opacity-60'}`}>
            <input
              type="checkbox"
              className="mt-1 w-5 h-5"
              disabled={!c.placable}
              checked={c.placable && choisies.has(c.id)}
              onChange={() => basculer(c.id)}
            />
            <span className="flex-1">
              <span className="font-medium text-gray-900">{c.customer_name}</span>{' '}
              <span className="text-xs text-gray-500">#{c.order_number}</span>
              {c.order_type === 'enfact_bl' && <span className="ml-2 text-xs text-sky-700">bon Enfact</span>}
              {c.en_retard && c.delivery_date && (
                <span className="block text-sm text-amber-800">
                  Prévue le {new Date(c.delivery_date).toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })} — reportée à ce jour
                </span>
              )}
              {c.delivery_address && (
                <span className="block text-sm text-gray-600">
                  {c.delivery_address.street}, {c.delivery_address.postal_code} {c.delivery_address.city}
                </span>
              )}
              {!c.placable && <span className="block text-sm text-amber-700">{raison(c)}</span>}
            </span>
          </label>
        </li>
      ))}
    </ul>
  );
}

/** « Nouvelle tournée » : choisir les commandes et le livreur. */
export function NouvelleTourneeModal({ open, date, couriers, livreurParDefaut, apiFetch, onClose, onCree }: {
  open: boolean;
  date: string;
  couriers: Array<{ id: string; name: string }>;
  livreurParDefaut: string;
  apiFetch: ApiFetch;
  onClose: () => void;
  onCree: () => void;
}) {
  const { commandes, choisies, basculer, erreur } = useCommandesAPlacer(apiFetch, date, open);
  const [livreur, setLivreur] = useState(livreurParDefaut);
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState('');

  useEffect(() => { if (open) { setLivreur(livreurParDefaut); setRefus(''); } }, [open, livreurParDefaut]);
  if (!open) return null;

  const nb = choisies.size;
  const creer = async () => {
    setEnvoi(true);
    setRefus('');
    try {
      const r = await apiFetch('/customer-deliveries/create-daily', {
        method: 'POST',
        body: JSON.stringify({ date, vendor_id: livreur || undefined, order_ids: Array.from(choisies) }),
      });
      const corps = await r.json().catch(() => ({}));
      if (!r.ok || corps?.success === false) {
        setRefus(corps?.message || `Refusé (HTTP ${r.status})`);
        return;
      }
      onCree();
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-xl w-full max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">Nouvelle tournée — {new Date(date + 'T12:00:00').toLocaleDateString('fr-BE', { weekday: 'long', day: 'numeric', month: 'long' })}</h2>
          <p className="text-sm text-gray-600 mt-1">Cochez les commandes de ce livreur. Les autres resteront à placer dans une seconde tournée.</p>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Livreur</span>
            <select value={livreur} onChange={(e) => setLivreur(e.target.value)} className="mt-1 w-full px-3 py-2.5 border border-gray-300 rounded-lg">
              <option value="">À définir plus tard</option>
              {couriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          {erreur && <p className="text-sm text-red-700">Liste illisible : {erreur}</p>}
          {commandes === null ? <p className="text-sm text-gray-500">Chargement…</p> : <ListeCommandes commandes={commandes} choisies={choisies} basculer={basculer} />}
          {refus && <p className="text-sm text-red-700">{refus}</p>}
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2.5 border border-gray-300 rounded-lg font-medium">Annuler</button>
          <button onClick={creer} disabled={nb === 0 || envoi} className="px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium disabled:opacity-50">
            {envoi ? 'Création…' : `Créer la tournée (${nb})`}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Dans le détail d'une tournée : ajouter des commandes restées à placer. */
export function AjouterCommandesTournee({ deliveryId, date, apiFetch, onAjoute }: {
  deliveryId: string;
  date: string;
  apiFetch: ApiFetch;
  onAjoute: () => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const { commandes, choisies, basculer, erreur } = useCommandesAPlacer(apiFetch, date, ouvert);
  const [envoi, setEnvoi] = useState(false);
  const [refus, setRefus] = useState('');

  if (!ouvert) {
    return (
      <button onClick={() => setOuvert(true)} className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">
        ＋ Ajouter des commandes à cette tournée
      </button>
    );
  }
  const ajouter = async () => {
    setEnvoi(true);
    setRefus('');
    try {
      const r = await apiFetch(`/customer-deliveries/${deliveryId}/stops`, {
        method: 'POST',
        body: JSON.stringify({ order_ids: Array.from(choisies) }),
      });
      if (!r.ok) {
        const corps = await r.json().catch(() => ({}));
        setRefus(corps?.message || `Refusé (HTTP ${r.status})`);
        return;
      }
      setOuvert(false);
      onAjoute();
    } finally {
      setEnvoi(false);
    }
  };
  return (
    <div className="space-y-3 border border-gray-200 rounded-lg p-4">
      <p className="font-medium text-gray-900">Commandes du jour hors tournée</p>
      {erreur && <p className="text-sm text-red-700">Liste illisible : {erreur}</p>}
      {commandes === null ? <p className="text-sm text-gray-500">Chargement…</p> : <ListeCommandes commandes={commandes} choisies={choisies} basculer={basculer} />}
      {refus && <p className="text-sm text-red-700">{refus}</p>}
      <div className="flex justify-end gap-3">
        <button onClick={() => setOuvert(false)} className="px-4 py-2 border border-gray-300 rounded-lg font-medium">Fermer</button>
        <button onClick={ajouter} disabled={choisies.size === 0 || envoi} className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium disabled:opacity-50">
          {envoi ? 'Ajout…' : `Ajouter (${choisies.size})`}
        </button>
      </div>
    </div>
  );
}
